import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { setAccess } from "../store/authSlice";
import { denyAccess } from "../api/access";
import { useSharedPinExit } from "../components/profileActions";

// Под тази граница (стар BE без полето, повреден отговор) се ползва default-ът.
const MIN_IDLE_SECONDS = 10;
const DEFAULT_IDLE_SECONDS = 30;
const IDLE_CHECK_INTERVAL_MS = 1_000;
// Последното действие в КОЙ ДА Е прозорец на терминала — служителят е един
// за всички прозорци (`employee` в localStorage), затова и неактивността се
// брои общо: работа в единия прозорец не бива да изпише служителя от другия.
export const LAST_ACTIVITY_KEY = "employee_last_activity";
const SHARED_ACTIVITY_WRITE_INTERVAL_MS = 5_000;
const ACTIVITY_EVENTS = ["pointermove", "keydown", "wheel", "touchstart"] as const;
// Адресите на мониторите (кухня/бар): пасивен екран, който
// никой не пипа с минути — изходът там би го оставил на ПИН клавиатурата.
const PASSIVE_SCREEN_PREFIXES = ["/monitors"];

const readSharedActivity = (): number => {
  try {
    return Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeSharedActivity = (timestamp: number) => {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(timestamp));
  } catch {
    // Без localStorage броим само този прозорец.
  }
};

/**
 * „Автоматичен изход до код" (настройка на ресторанта
 * `orderingAutoCodeLogout`, Конфигурация → Маркиране). Работи САМО за
 * служител, влязъл с код на общ терминал (`useSharedPinExit` е null във
 * всеки друг случай — личен акаунт с име и парола, ПИН екранът) и само при
 * включена настройка, докато екранът на служителя наистина е отворен
 * (`employeeScreenShown` — не и при ПИН клавиатурата, възстановяване или
 * ограничен локален режим). Избраното време (`orderingAutoCodeLogoutSeconds`,
 * по подразбиране 30 s) без докосване, мишка, клавиш или колелце →
 * същият изход като „Изход" в профилното меню (споделената сесия остава).
 *
 * Не прекъсва работа в ход: докато се закрива сметка (`bills.closingBills` —
 * чакането на фискалния принтер/пинпада) или тече запис през React Query, или
 * пръстът/бутонът на мишката е натиснат (гласовата поръчка се държи), това
 * се брои за действие и времето започва да тече, след като свърши.
 * В тестов режим първо се сваля той (както Esc стълбицата) — иначе
 * `access` остава по споделената сесия за следващия служител.
 */
export function useAutoCodeLogout(employeeScreenShown: boolean): void {
  const enabled = useAppSelector(
    (s) => s.auth.user?.configuration?.orderingAutoCodeLogout === true,
  );
  const configuredSeconds = useAppSelector(
    (s) => s.auth.user?.configuration?.orderingAutoCodeLogoutSeconds,
  );
  const idleLimitMs =
    (typeof configuredSeconds === "number" && configuredSeconds >= MIN_IDLE_SECONDS
      ? configuredSeconds
      : DEFAULT_IDLE_SECONDS) * 1000;
  const pinExit = useSharedPinExit();
  const access = useAppSelector((s) => s.auth.access);
  const closingBillsCount = useAppSelector((s) => s.bills.closingBills.length);
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const onPassiveScreen = PASSIVE_SCREEN_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix),
  );

  // Стойностите, които таймерът чете в момента на проверката — без да се
  // рестартира броенето при всеки рендер.
  const pinExitRef = useRef(pinExit);
  pinExitRef.current = pinExit;
  const accessRef = useRef(access);
  accessRef.current = access;
  const closingBillsRef = useRef(closingBillsCount);
  closingBillsRef.current = closingBillsCount;

  const active =
    enabled && employeeScreenShown && pinExit !== null && !onPassiveScreen;

  useEffect(() => {
    if (!active) return;
    let lastActivityAt = Date.now();
    let lastSharedWriteAt = 0;
    let pointerPressed = false;
    let exiting = false;

    const markActivity = () => {
      const now = Date.now();
      lastActivityAt = now;
      if (now - lastSharedWriteAt >= SHARED_ACTIVITY_WRITE_INTERVAL_MS) {
        lastSharedWriteAt = now;
        writeSharedActivity(now);
      }
    };
    const onPointerDown = () => {
      pointerPressed = true;
      markActivity();
    };
    const onPointerRelease = () => {
      pointerPressed = false;
      markActivity();
    };

    // Влизането (или включването на настройката) е действие — броенето
    // започва от сега, не от последния запис на предишния служител.
    markActivity();
    // capture: модал или поле, което спира събитието, пак е действие.
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("pointerup", onPointerRelease, true);
    window.addEventListener("pointercancel", onPointerRelease, true);
    ACTIVITY_EVENTS.forEach((eventName) =>
      window.addEventListener(eventName, markActivity, true),
    );

    const isWorkInProgress = () =>
      pointerPressed || closingBillsRef.current > 0 || queryClient.isMutating() > 0;

    const exitToCode = async () => {
      const exit = pinExitRef.current;
      if (!exit) return;
      exiting = true;
      if (accessRef.current) {
        try {
          await denyAccess();
        } catch (error) {
          console.error("Deny access error:", error);
        }
        dispatch(setAccess(false));
      }
      exit();
    };

    const intervalId = window.setInterval(() => {
      if (exiting) return;
      if (isWorkInProgress()) {
        markActivity();
        return;
      }
      const latestActivityAt = Math.max(lastActivityAt, readSharedActivity());
      if (Date.now() - latestActivityAt < idleLimitMs) return;
      void exitToCode();
    }, IDLE_CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("pointerup", onPointerRelease, true);
      window.removeEventListener("pointercancel", onPointerRelease, true);
      ACTIVITY_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, markActivity, true),
      );
    };
  }, [active, idleLimitMs, dispatch, queryClient]);
}
